/**
 * `/app` — the client portal's layout route.
 *
 * The guard is `guardClientPortal`; the shell is `PortalLayout`. The only thing
 * this file adds is the one notice a guard can raise: a client user who followed a
 * link into `/studio` is sent here with `?notice=staff-only`, because a 403 page
 * for somebody who did nothing wrong is a dead end and their own portal is one
 * redirect away.
 *
 * Dismissing it removes the parameter, which is what makes it a fact about this
 * navigation rather than a piece of state somebody has to store.
 *
 * **`routeSearch`, and on this route it matters most.** This is the layout every
 * client page hangs under, so a `validateSearch` that threw here took the whole
 * portal down for any address carrying a `notice` this file does not know — one
 * stale link, and the sidebar goes with it. A notice nobody recognises is no
 * notice: the page renders without one.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { guardClientPortal, STAFF_ONLY_NOTICE } from '../auth/guards.js'
import { NotFound } from '../errors/NotFound.js'
import { routeSearch } from '../search/useSearchState.js'
import { PortalLayout } from '../shell/PortalLayout.js'

const AppSearchSchema = z.object({
  notice: z.literal(STAFF_ONLY_NOTICE).optional(),
})

export const Route = createFileRoute('/app')({
  validateSearch: routeSearch(AppSearchSchema),
  beforeLoad: ({ context, location }) => {
    guardClientPortal(context.session.principal, location.href)
  },
  /**
   * A path under this portal that does not exist keeps the portal: the nav is
   * still the way out. `__root.tsx`'s own `notFoundComponent` is for an address
   * above both portals, where nothing has said which shell it would be.
   */
  notFoundComponent: NotFound,
  component: ClientPortal,
})

function ClientPortal() {
  const { notice } = Route.useSearch()
  const navigate = useNavigate()

  if (notice !== STAFF_ONLY_NOTICE) return <PortalLayout />

  return (
    <PortalLayout
      notice={
        <div className="notice" role="status">
          <div className="row">
            <span>
              <b>The studio portal is for Far East Studios staff.</b> This is your portal.
            </span>
            <button
              type="button"
              className="btn btn--sm btn--ghost push"
              onClick={() => {
                void navigate({ to: '/app', search: {}, replace: true })
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      }
    />
  )
}
